import { EntityNotLoaded, createLoadedEntity, isEntityLoaded, type Entity } from '@/Models/Entity';
import { Label } from '@/Models/GenericWorklist/Label';
import { type Language } from '@/Models/GenericWorklist/Language';
import type PatientOriginFilter from '@/Models/GenericWorklist/PatientOriginFilter';
import type PatientStatusFilter from '@/Models/GenericWorklist/PatientsStatusFilter';
import type ReportStatusFilter from '@/Models/GenericWorklist/ReportStatusFilter';
import ReportParticipantType, { ALL_REPORT_PARTICIPANT_TYPES } from '@/Models/GenericWorklist/ReportParticipantType';
import { type Room, type SiteInfo } from '@/Models/GenericWorklist/Room';
import { type WorklistFilterParams } from '@/Models/GenericWorklist/WorklistParams';
import { type WorklistInitialData } from '@/Models/GenericWorklist/WorklistSideBarData';
import type Status from '@/Models/Shared/Status';
import { Urgency } from '@/Models/GenericWorklist/Urgency';
import Modality from '@/Models/Modality';
import * as API from '@/API';
import { InvoiceStatus } from '@/Components/Invoicing/InvoiceStatus';
import { JustificationFilterOptions } from '@/Models/GenericWorklist/JustificationFilterOptions';
import { t } from 'i18next';
import { IWorklistSidebarStore } from '../WorklistSidebarStore';
import { StatusModel } from '@/Models/GenericWorklist/StatusModel';

export default class WorklistFilterHelper {
    readonly sidebarStore: IWorklistSidebarStore;

    constructor(sidebarStore: IWorklistSidebarStore) {
        this.sidebarStore = sidebarStore;
    }

    get patientFirstNameFilterValue(): string {
        return this.currentFilter?.PatientFirstName ?? '';
    }

    get orderNumberFilterValue(): string {
        return this.currentFilter?.OrderNumber ?? '';
    }

    get requestingPhysicianFirstNameFilterValue(): string {
        return this.currentFilter?.RequestingPhysicianFirstName ?? '';
    }

    get requestingPhysicianLastNameFilterValue(): string {
        return this.currentFilter?.RequestingPhysicianLastName ?? '';
    }

    get patientLastNameFilterValue(): string {
        return this.currentFilter?.PatientLastName ?? '';
    }

    get patientDoBFilterValue(): Date | undefined {
        return this.currentFilter?.DoB;
    }

    get visitNumberValue(): string {
        return this.currentFilter?.VisitNumber ?? '';
    }

    get unitOfCareFilterValue(): string {
        return this.currentFilter?.UnitOfCare ?? '';
    }

    get patientExternalIDFilterValue(): string {
        return this.currentFilter?.ExternalID ?? '';
    }

    get patientNationalNumberFilterValue(): string {
        return this.currentFilter?.PatientNationalNumber ?? '';
    }

    get selectedSites(): SiteInfo[] {
        const selectedSiteIDs = this.currentFilter?.SiteIDs;
        return this.allSites.filter((site) => selectedSiteIDs?.find((selectedSiteID) => selectedSiteID === site.ID));
    }

    get selectedModalities(): Modality[] {
        const selectedModalityIDs = this.currentFilter?.ModalityIDs;
        return this.allModalities.filter((modality) => selectedModalityIDs?.find((selectedModalityID) => selectedModalityID === modality.ID));
    }

    get selectedProcedureCodes(): string[] | undefined {
        return this.currentFilter?.ProcedureCodes;
    }

    get selectedRadiologists(): { ID: number; FullName: string }[] {
        return this.currentFilter?.Radiologists ?? [];
    }

    get selectedTechnicians(): { ID: number; FullName: string }[] {
        return this.currentFilter?.Technicians ?? [];
    }

    get selectedReportModifiedBy(): { ID: number; FullName: string }[] {
        return this.currentFilter?.ReportModifiedBy ?? [];
    }

    get selectedTranscriptionists(): { ID: number; FullName: string }[] {
        return this.currentFilter?.Transcriptionists ?? [];
    }

    get selectedReportParticipants(): { ID: number; FullName: string }[] {
        return this.currentFilter?.ReportParticipants ?? [];
    }

    get selectedReportParticipantTypes(): ReportParticipantType[] {
        const selectedTypes = this.currentFilter?.ReportParticipantTypes;
        return this.allReportParticipantTypes.filter((type) =>
            selectedTypes?.some((selectedType) => Number(selectedType) === Number(type)),
        );
    }

    get selectedProcedureStatuses(): StatusModel[] {
        const selectedProcedureStatuses = this.currentFilter?.ProcedureStatuses;
        return this.allProcedureStatuses.filter((status) => selectedProcedureStatuses?.find((selectedStatus) => selectedStatus === status.ID));
    }

    get selectedStatuses(): PatientStatusFilter[] {
        const selectedStatuses = this.currentFilter?.PatientStatuses;
        return this.allPatientStatuses.filter((status) => selectedStatuses?.find((selectedStatus) => selectedStatus === status));
    }

    get selectedInvoiceStatuses(): InvoiceStatus[] {
        const selectedStatuses = this.currentFilter?.InvoiceStatuses;
        return this.allInvoiceStatuses.filter((status) =>
            selectedStatuses?.some((selectedStatus) => Number(selectedStatus) === Number(status)),
        );
    }

    get selectedReportStatuses(): ReportStatusFilter[] {
        const selectedStatuses = this.currentFilter?.ReportStatuses;
        return this.allReportStatuses.filter((status) =>
            selectedStatuses?.some((selectedStatus) => Number(selectedStatus) === Number(status)),
        );
    }

    get selectedOrderStatuses(): StatusModel[] {
        const selectedOrderStatuses = this.currentFilter?.OrderStatuses;
        return this.allOrderStatuses.filter((status) => selectedOrderStatuses?.find((selectedStatus) => selectedStatus === status.ID));
    }

    get selectedPatientOrigins(): PatientOriginFilter[] {
        const selectedOriginIDs = this.currentFilter?.PatientOrigins;
        return this.allPatientOrigins.filter((origin) => selectedOriginIDs?.find((selectedOriginID) => selectedOriginID === origin));
    }

    get selectedPatientLanguages(): Language[] {
        const selectedLanguageIDs = this.currentFilter?.PatientLanguageIDs;
        return this.allPatientLanguages.filter((language) => selectedLanguageIDs?.find((selectedLanguageID) => selectedLanguageID === language.ID));
    }

    get allSites(): SiteInfo[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Sites;
    }

    get allModalities(): Modality[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Modalities;
    }

    get allProcedureStatuses(): StatusModel[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Statuses;
    }

    get allPatientStatuses(): PatientStatusFilter[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.PatientStatuses;
    }

    get allInvoiceStatuses(): InvoiceStatus[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.InvoiceStatuses;
    }

    get allReportStatuses(): ReportStatusFilter[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.ReportStatuses;
    }

    get allReportParticipantTypes(): ReportParticipantType[] {
        return ALL_REPORT_PARTICIPANT_TYPES;
    }

    get allOrderStatuses(): StatusModel[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.OrderStatuses;
    }

    get allPatientOrigins(): PatientOriginFilter[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.PatientOrigins;
    }

    get allPatientLanguages(): Language[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Languages;
    }

    get selectedRooms(): SiteInfo[] {
        const selectedRoomIDs = this.currentFilter?.RoomIDs;
        return this.allRooms.filter((room) => selectedRoomIDs?.find((selectedRoomID) => selectedRoomID === room.ID));
    }

    get selectedLabelsToInclude(): Label[] {
        const selectedLabelIDs = this.currentFilter?.LabelsToIncludeIDs;
        return this.allLabelsToInclude.filter((label) => selectedLabelIDs?.find((selectedLabelID) => selectedLabelID === label.ID));
    }

    get selectedLabelsToExclude(): Label[] {
        const selectedLabelIDs = this.currentFilter?.LabelsToExcludeIDs;
        return this.allLabelsToExclude.filter((label) => selectedLabelIDs?.find((selectedLabelID) => selectedLabelID === label.ID));
    }

    get selectedUrgencies(): Urgency[] {
        const selectedUrgencyIDs = this.currentFilter?.UrgencyIDs;
        return this.allUrgencies.filter((urgency) => selectedUrgencyIDs?.find((selectedUrgencyID) => selectedUrgencyID === urgency.ID));
    }

    get allRooms(): Room[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Rooms;
    }

    get allLabelsToInclude(): Label[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.LabelsToInclude;
    }

    get allLabelsToExclude(): Label[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.LabelsToExclude;
    }

    get allUrgencies(): Urgency[] {
        if (!isEntityLoaded(this.initialPageData)) {
            return [];
        }
        return this.initialPageData.Urgencies.filter((urgency) => urgency.Name && urgency.Name.trim() !== '');
    }

    get accessionNumberFilterValue(): string {
        return this.currentFilter?.AccessionNumber ?? '';
    }

    get procedureDescriptionFilterValue(): string {
        return this.currentFilter?.ProcedureDescription ?? '';
    }

    get dateRangePreset(): string | undefined {
        return this.currentFilter?.DateRangePreset;
    }

    get from(): Date | undefined {
        return this.currentFilter?.From ?? undefined;
    }

    get until(): Date | undefined {
        return this.currentFilter?.Until ?? undefined;
    }

    get reportCreatedDateRangePreset(): string | undefined {
        return this.currentFilter?.ReportCreatedDateRangePreset;
    }

    get reportCreatedFrom(): Date | undefined {
        return this.currentFilter?.ReportCreatedFrom ?? undefined;
    }

    get reportCreatedUntil(): Date | undefined {
        return this.currentFilter?.ReportCreatedUntil ?? undefined;
    }

    get reportModifiedDateRangePreset(): string | undefined {
        return this.currentFilter?.ReportModifiedDateRangePreset;
    }

    get reportModifiedFrom(): Date | undefined {
        return this.currentFilter?.ReportModifiedFrom ?? undefined;
    }

    get reportModifiedUntil(): Date | undefined {
        return this.currentFilter?.ReportModifiedUntil ?? undefined;
    }

    get filtersAreDisabled(): boolean {
        if (!isEntityLoaded(this.initialPageData)) {
            return true;
        }

        if (!isEntityLoaded(this.sidebarStore.worklistStore.results)) {
            return true;
        }

        return false;
    }

    setPatientLastNameSearchTermFilter = (value: string): void => {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientLastName: value,
        });
    };

    setPatientFirstNameSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientFirstName: value,
        });
    }

    setOrderNumberSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            OrderNumber: value,
        });
    }

    setRequestingPhysicianFirstNameSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            RequestingPhysicianFirstName: value,
        });
    }

    setRequestingPhysicianLastNameSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            RequestingPhysicianLastName: value,
        });
    }

    setUnitOfCareSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            UnitOfCare: value,
        });
    }

    setDoBSearchTermFilter(value: Date | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            DoB: value ?? undefined,
        });
    }

    setVisitNumberFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            VisitNumber: value,
        });
    }

    setExternalIDTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ExternalID: value,
        });
    }

    setNationalNumberTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientNationalNumber: value,
        });
    }

    setSelectedSites(siteIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            SiteIDs: siteIDs ?? [],
        });
    }

    setSelectedModalities(modalityIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ModalityIDs: modalityIDs ?? [],
        });
    }

    setSelectedRadiologists(radiologists: { ID: number; FullName: string }[]): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            Radiologists: radiologists ?? [],
        });
    }

    setSelectedTechnicians(technicians: { ID: number; FullName: string }[]): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            Technicians: technicians ?? [],
        });
    }

    setSelectedReportModifiedBy(users: { ID: number; FullName: string }[]): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportModifiedBy: users ?? [],
        });
    }

    setSelectedTranscriptionists(users: { ID: number; FullName: string }[]): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            Transcriptionists: users ?? [],
        });
    }

    setReportParticipants(users: { ID: number; FullName: string }[]): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportParticipants: users ?? [],
        });
    }

    setSelectedReportParticipantTypes(types: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportParticipantTypes: types ?? [],
        });
    }

    setSelectedProcedureCodes(procedureCodes: string[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ProcedureCodes: procedureCodes ?? [],
        });
    }

    setSelectedRooms(roomIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            RoomIDs: roomIDs ?? [],
        });
    }

    setSelectedLabelsToInclude(labelsIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            LabelsToIncludeIDs: labelsIDs ?? [],
        });
    }

    setSelectedLabelsToExclude(labelsIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            LabelsToExcludeIDs: labelsIDs ?? [],
        });
    }

    setSelectedProcedureStatuses(procedureStatuses: Status[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ProcedureStatuses: procedureStatuses ?? [],
        });
    }

    setSelectedPatientStatuses(patientStatuses: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientStatuses: patientStatuses ?? [],
        });
    }

    setSelectedOrderStatuses(orderStatuses: Status[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            OrderStatuses: orderStatuses ?? [],
        });
    }

    setSelectedPatientOrigins(patientOrigins: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientOrigins: patientOrigins ?? [],
        });
    }

    setSelectedPatientLanguages(patientLanguageIDs: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            PatientLanguageIDs: patientLanguageIDs ?? [],
        });
    }

    setSelectedUrgencies(urgencyIds: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            UrgencyIDs: urgencyIds ?? [],
        });
    }

    setSelectedInvoiceStatuses(statuses: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            InvoiceStatuses: statuses ?? [],
        });
    }

    setSelectedReportStatuses(statuses: number[] | null): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportStatuses: statuses ?? [],
        });
    }

    setDateRangePreset(preset: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            DateRangePreset: preset,
            From: undefined,
            Until: undefined,
        });
    }

    setDateRange(from?: Date, until?: Date): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            DateRangePreset: undefined,
            From: from,
            Until: until,
        });
    }

    setReportCreatedDateRangePreset(preset: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportCreatedDateRangePreset: preset,
            ReportCreatedFrom: undefined,
            ReportCreatedUntil: undefined,
        });
    }

    setReportCreatedDateRange(from?: Date, until?: Date): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportCreatedDateRangePreset: undefined,
            ReportCreatedFrom: from,
            ReportCreatedUntil: until,
        });
    }

    setReportModifiedDateRangePreset(preset: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportModifiedDateRangePreset: preset,
            ReportModifiedFrom: undefined,
            ReportModifiedUntil: undefined,
        });
    }

    setReportModifiedDateRange(from?: Date, until?: Date): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportModifiedDateRangePreset: undefined,
            ReportModifiedFrom: from,
            ReportModifiedUntil: until,
        });
    }

    setReportCreatedDateRangeUpdate(update?: { from?: Date; until?: Date; preset?: string }): void {
        if (update?.preset) {
            this.setReportCreatedDateRangePreset(update.preset);
        }
        else {
            this.setReportCreatedDateRange(update?.from, update?.until);
        }
    }

    setReportModifiedDateRangeUpdate(update?: { from?: Date; until?: Date; preset?: string }): void {
        if (update?.preset) {
            this.setReportModifiedDateRangePreset(update.preset);
        }
        else {
            this.setReportModifiedDateRange(update?.from, update?.until);
        }
    }

    setAccessionNumberSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            AccessionNumber: value,
        });
    }

    setProcedureDescriptionSearchTermFilter(value: string): void {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ProcedureDescription: value,
        });
    }

    get currentFilter(): WorklistFilterParams | undefined {
        return this.sidebarStore.currentFilter;
    }

    private get initialPageData(): Entity<WorklistInitialData> {
        const initialPageData = this.sidebarStore.worklistStore.initialPageData;
        if (isEntityLoaded(initialPageData)) {
            return createLoadedEntity(initialPageData);
        }

        return EntityNotLoaded.instance;
    }

    get visibleRoomOptions() {
        const allRoomsMultiselectOptions = this.allRooms.map((room) => ({ value: room.ID, label: room.Name }));
        const selectedRoomsMultiselectOptions = this.selectedRooms.map((room) => ({ value: room.ID, label: room.Name }));
        const selectedSites = this.selectedSites;

        if (selectedSites.length === 0) {
            return allRoomsMultiselectOptions;
        }

        const selectedSiteIDs = selectedSites.map((site) => site.ID);
        const roomMap = new Map(this.allRooms.map((r) => [r.ID, r]));

        const roomsFromSelectedSites = allRoomsMultiselectOptions.filter((roomOption) => {
            const roomData = roomMap.get(roomOption.value);
            return roomData?.SiteInfo && selectedSiteIDs.includes(roomData.SiteInfo.ID);
        });

        const preservedSelectedRooms = selectedRoomsMultiselectOptions.filter((selectedRoom) =>
            !roomsFromSelectedSites.some((r) => r.value === selectedRoom.value),
        );

        return [...roomsFromSelectedSites, ...preservedSelectedRooms].filter(
            (room, idx, self) => idx === self.findIndex((r) => r.value === room.value),
        );
    }

    async loadProcedureCodes(searchTerm: string): Promise<{ value: string; label: string }[]> {
        if (!searchTerm || searchTerm.trim() === '') {
            return [];
        }

        return API.procedures.procedureCodes(searchTerm)
            .then((result) => result.map((code: string | number) => ({
                value: String(code),
                label: String(code),
            })))
            .catch(() => []);
    }

    async loadRadiologists(searchTerm: string, currentUserTranslation: string): Promise<{ value: number; label: string }[]> {
        if (!searchTerm || searchTerm.trim() === '') {
            return [];
        }

        return API.radiologists.search(searchTerm)
            .then((res) => {
                const currentUserOptionId = 0;
                let results = res.map((x) => ({ value: x.Id, label: x.FullName }));
                if (currentUserTranslation.toLowerCase().includes(searchTerm.toLowerCase())) {
                    results = [{ value: currentUserOptionId, label: currentUserTranslation }, ...results];
                }
                return results;
            });
    }

    async loadTechnicians(searchTerm: string): Promise<{ value: number; label: string }[]> {
        if (!searchTerm || searchTerm.trim() === '') {
            return [];
        }

        return API.technicians.search(searchTerm)
            .then((res) => {
                return res.map((x) => ({ value: x.ID, label: x.FullText }));
            });
    }

    async loadReportModifiedByUsers(searchTerm: string): Promise<{ value: number; label: string }[]> {
        return this.loadUsers(searchTerm);
    }

    async loadReportParticipants(searchTerm: string): Promise<{ value: number; label: string }[]> {
        const currentUserOption = { value: 0, label: t('CurrentUser') };

        if (!searchTerm || searchTerm.trim() === '') {
            return [currentUserOption];
        }

        const results = await API.radiologists.search(searchTerm)
            .then((res) => res.map((x) => ({ value: x.Id, label: x.FullName })))
            .catch(() => []);

        const alreadyHasCurrentUser = results.some((r) => r.value === 0);
        return alreadyHasCurrentUser ? results : [currentUserOption, ...results];
    }

    async loadTranscriptionists(searchTerm: string): Promise<{ value: number; label: string }[]> {
        return this.loadUsers(searchTerm);
    }

    private async loadUsers(searchTerm: string): Promise<{ value: number; label: string }[]> {
        const currentUserOption = { value: 0, label: t('CurrentUser') };

        if (!searchTerm || searchTerm.trim() === '') {
            return [currentUserOption];
        }

        const results = await API.users.GetByFullUsername(searchTerm)
            .then((res) => res.map((x) => ({ value: x.Id, label: x.FullName })))
            .catch(() => []);

        const alreadyHasCurrentUser = results.some((r) => r.value === 0);
        return alreadyHasCurrentUser ? results : [currentUserOption, ...results];
    }

    get readyForInvoicingFilterValue(): string {
        const value = this.currentFilter?.ReadyForInvoicing;
        if (value === true) {
            return 'true';
        }
        if (value === false) {
            return 'false';
        }
        return 'all';
    }

    setReadyForInvoicingFilter = (value: string): void => {
        let booleanValue: boolean | undefined;

        switch (value) {
            case 'true':
                booleanValue = true;
                break;
            case 'false':
                booleanValue = false;
                break;
            case 'all':
            default:
                booleanValue = undefined;
                break;
        }

        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReadyForInvoicing: booleanValue,
        });
    };

    get readyForInvoicingOptions() {
        return this.yesNoOptions;
    }

    get reportLockedFilterValue(): string {
        const value = this.currentFilter?.ReportLocked;
        if (value === true) {
            return 'true';
        }
        if (value === false) {
            return 'false';
        }
        return 'all';
    }

    setReportLockedFilter = (value: string): void => {
        let booleanValue: boolean | undefined;

        switch (value) {
            case 'true':
                booleanValue = true;
                break;
            case 'false':
                booleanValue = false;
                break;
            case 'all':
            default:
                booleanValue = undefined;
                break;
        }

        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportLocked: booleanValue,
        });
    };

    get reportLockedOptions() {
        return this.yesNoOptions;
    }

    get reportAvailableFilterValue(): string {
        const value = this.currentFilter?.ReportAvailable;
        if (value === true) {
            return 'true';
        }
        if (value === false) {
            return 'false';
        }
        return 'all';
    }

    setReportAvailableFilter = (value: string): void => {
        let booleanValue: boolean | undefined;

        switch (value) {
            case 'true':
                booleanValue = true;
                break;
            case 'false':
                booleanValue = false;
                break;
            case 'all':
            default:
                booleanValue = undefined;
                break;
        }

        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            ReportAvailable: booleanValue,
        });
    };

    get reportAvailableOptions() {
        return this.yesNoOptions;
    }

    get yesNoOptions() {
        return [
            { value: 'all', label: t('All') },
            { value: 'true', label: t('Yes') },
            { value: 'false', label: t('No') },
        ];
    }

    get justificationFilterValue(): JustificationFilterOptions {
        const value = this.currentFilter?.Justification;
        return value ?? JustificationFilterOptions.All;
    }

    setJustificationFilter = (value: JustificationFilterOptions | null): void => {
        this.sidebarStore.setCurrentFilter({
            ...this.sidebarStore.currentFilter,
            Justification: value ?? JustificationFilterOptions.All,
        });
    };

    get justificationOptions() {
        return [
            { value: JustificationFilterOptions.All, label: t('All') },
            { value: JustificationFilterOptions.Required, label: t('Required') },
            { value: JustificationFilterOptions.Available, label: t('Available') },
        ];
    }
}
